import type { MenuNode } from "@/lib/wp";
import { DEFAULT_LOCALE, type Locale } from "@/hooks/use-locale";

export type MenuLocation = "header" | "footer";
export type MenuPayload = Partial<Record<MenuLocation, MenuNode[]>>;
export type MenuPreloadStore = Record<string, MenuPayload>;

const GLOBAL_KEY = "__PRERENDER_MENUS__";
const SCRIPT_PREFIX = "__PR_MENUS_";
const MENU_LOCATIONS: MenuLocation[] = ["header", "footer"];

function getWindowObject(): (Window & { [GLOBAL_KEY]?: MenuPreloadStore }) | null {
  if (typeof window === "undefined") {
    return null;
  }
  return window as Window & { [GLOBAL_KEY]?: MenuPreloadStore };
}

function readStore(): MenuPreloadStore | null {
  const win = getWindowObject();
  if (!win) return null;
  const value = win[GLOBAL_KEY];
  if (!value || typeof value !== "object") {
    return null;
  }
  return value as MenuPreloadStore;
}

function writeStore(store: MenuPreloadStore) {
  const win = getWindowObject();
  if (!win) return;
  win[GLOBAL_KEY] = store;
}

function sanitizePayload(raw: unknown): MenuPayload {
  if (!raw || typeof raw !== "object") {
    return {};
  }
  const payload = raw as Record<string, unknown>;
  const cleaned: MenuPayload = {};
  for (const location of MENU_LOCATIONS) {
    const nodes = payload[location];
    if (Array.isArray(nodes)) {
      cleaned[location] = nodes as MenuNode[];
    }
  }
  return cleaned;
}

function mergeStore(existing: MenuPreloadStore | null, updates: MenuPreloadStore): MenuPreloadStore {
  const base: MenuPreloadStore = existing ? { ...existing } : {};
  for (const [rawKey, rawPayload] of Object.entries(updates || {})) {
    if (!rawPayload || typeof rawPayload !== "object") {
      continue;
    }
    const key = rawKey.toLowerCase();
    const sanitized = sanitizePayload(rawPayload);
    if (!base[key]) {
      base[key] = {};
    }
    for (const location of MENU_LOCATIONS) {
      const nodes = sanitized[location];
      if (Array.isArray(nodes)) {
        base[key]![location] = nodes;
      }
    }
  }
  return base;
}

function updateStore(updates: MenuPreloadStore) {
  if (!updates || Object.keys(updates).length === 0) {
    return;
  }
  const merged = mergeStore(readStore(), updates);
  writeStore(merged);
}

export function localeKeyForMenu(locale: Locale): string {
  return locale === DEFAULT_LOCALE ? DEFAULT_LOCALE : locale;
}

export function getPreloadedMenuNodes(localeKey: string, location: MenuLocation): MenuNode[] | null {
  const normalizedKey = (localeKey || "").toLowerCase();
  const store = readStore();
  const fromStore = store?.[normalizedKey]?.[location];
  if (Array.isArray(fromStore)) {
    return fromStore;
  }

  if (typeof document === "undefined") {
    return null;
  }
  const scriptId = `${SCRIPT_PREFIX}${localeKey.toUpperCase()}`;
  const script = document.getElementById(scriptId);
  if (!script?.textContent) {
    return null;
  }

  try {
    const parsed = JSON.parse(script.textContent);
    const payload = { [normalizedKey]: sanitizePayload(parsed) } satisfies MenuPreloadStore;
    updateStore(payload);
    const updated = readStore();
    const nodes = updated?.[normalizedKey]?.[location];
    return Array.isArray(nodes) ? nodes : null;
  } catch {
    return null;
  }
}

export function setPreloadedMenuNodes(localeKey: string, location: MenuLocation, nodes: MenuNode[]): void {
  if (!Array.isArray(nodes)) {
    return;
  }
  const normalizedKey = (localeKey || "").toLowerCase();
  updateStore({ [normalizedKey]: { [location]: nodes } });
}

export function getMenuPayloadSnapshot(): MenuPreloadStore | null {
  const store = readStore();
  if (!store) {
    return null;
  }
  const snapshot: MenuPreloadStore = {};
  for (const [key, rawPayload] of Object.entries(store)) {
    const sanitized = sanitizePayload(rawPayload);
    if (Object.keys(sanitized).length === 0) {
      continue;
    }
    snapshot[key] = sanitized;
  }
  return Object.keys(snapshot).length === 0 ? null : snapshot;
}

export function safeStringifyForScript(value: unknown): string {
  const json = JSON.stringify(value ?? {});
  return json
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}
